import React, { useRef, useState } from 'react';
import {
  ID_PHOTO_MAX_BYTES,
  ID_PHOTO_TYPES,
  IdDocType,
  IdFields,
  IdRecordBody,
  IdScanMode,
  IdScanResult,
  StayPublicError,
} from '../../../../api/stays-public-client';
import { StatusLabel } from '../../../../motion';
import { SegmentedControl } from '../../../segmented-control/segmented-control';
import { BirthDateField } from '../../../shared/birth-date-field/birth-date-field';
import { BirthParts, checkBirth, cleanDay, cleanYear, EMPTY_BIRTH } from '../../../shared/birth-date-field/birth-date-model';
import { COUNTRY_OPTIONS } from '../../../shared/country-select/countries';
import { ErrorText, Field, FieldError, FieldHint, Input, Label, Select } from '../../../onboarding/onboarding.styled';
import { CheckRow, Ghost, PartyActions, Primary, Prose, Quiet, Secondary } from '../../../onboarding/stay/public-stay-page.styled';
import { t } from '../../../onboarding/stay/stay-strings';
import { Legend, PersonGrid } from './guest-checkin.styled';

const L = 'en' as const;

/** After this many unreadable photos the form to type comes first (PLAN-IS0 "What you can present"). */
export const READS_BEFORE_MANUAL = 3;
/** The longest side a photo is shrunk to before it is sent (a passport page reads well at this size). */
const MAX_SIDE_PX = 2400;
/** Photos at or under this size go as they are. */
const SEND_AS_IS_BYTES = 2.5 * 1024 * 1024;

type Kind = 'passport' | 'id_card' | 'other';
const KIND_TO_DOC: Record<Kind, IdDocType> = { passport: 'passport', id_card: 'id_card', other: 'driving_license' };
const docToKind = (d: IdDocType | null | undefined, fallback: Kind): Kind =>
  d === 'passport' ? 'passport' : d === 'id_card' ? 'id_card' : d === 'driving_license' ? 'other' : fallback;

type Phase = 'consent' | 'choose' | 'reading' | 'confirm';

interface Draft {
  first_name: string;
  last_name: string;
  doc_number: string;
  doc_country: string;
  nationality: string;
  birth: BirthParts;
  expiry: BirthParts;
}

const EMPTY_DRAFT: Draft = {
  first_name: '',
  last_name: '',
  doc_number: '',
  doc_country: '',
  nationality: '',
  birth: EMPTY_BIRTH,
  expiry: EMPTY_BIRTH,
};

const toParts = (ymd: string | null | undefined): BirthParts => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');

  return m ? { day: String(Number(m[3])), month: String(Number(m[2])), year: m[1] } : EMPTY_BIRTH;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/** A real calendar day from three parts, or null (no age rules: an expiry date). */
const partsToYmd = (p: BirthParts): string | null => {
  const y = Number(cleanYear(p.year));
  const m = Number(p.month);
  const d = Number(cleanDay(p.day));
  if (String(y).length !== 4 || !(m >= 1 && m <= 12) || !(d >= 1)) return null;
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  if (d > last) return null;

  return `${y}-${pad2(m)}-${pad2(d)}`;
};

/** One string of every value, to tell whether the person changed what the photo gave. */
const valuesOf = (d: Draft) =>
  [d.first_name.trim(), d.last_name.trim(), d.doc_number.trim(), d.doc_country, d.nationality, partsToYmd(d.birth), partsToYmd(d.expiry)].join(
    '|'
  );

const draftOf = (f: IdFields): Draft => ({
  first_name: f.first_name || '',
  last_name: f.last_name || '',
  doc_number: f.doc_number || '',
  doc_country: f.doc_country || '',
  nationality: f.nationality || '',
  birth: toParts(f.birth_date),
  expiry: toParts(f.expiry_date),
});

/**
 * The photo, smaller when it is large (a 12-megapixel phone photo can pass the
 * 6 MB cap). Done in the browser's memory; never stored. Falls back to the
 * original file when the browser cannot draw it (an old browser, jsdom).
 */
async function shrinkPhoto(file: File): Promise<Blob> {
  if (file.size <= SEND_AS_IS_BYTES && ID_PHOTO_TYPES.includes(file.type)) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_SIDE_PX / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const out = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    canvas.width = 0;
    canvas.height = 0;

    return out || file;
  } catch {
    return file;
  }
}

/**
 * ONE PERSON'S ID, READ ON THIS PAGE (Independent Stays IS0, R-IS0.1-3).
 *
 * The privacy notice (when not yet agreed) → the kind of document → "Take a
 * photo" (the phone's camera through a file input, no app) or "Enter the
 * details yourself" → "Is this right?" with every field editable → Confirm.
 * The photo goes to the server once and is deleted there straight away;
 * nothing is kept until the person confirms. Used by the lead's check-in
 * (identity-step.tsx) and by one adult's own page (stay-guest-page.tsx); the
 * two differ only in the calls they pass.
 */
export const InhouseIdPanel: React.FC<{
  idPrefix: string;
  first: string;
  consent: { text: string; version: string; given: boolean };
  scanMode: IdScanMode;
  /** The check-in day ('YYYY-MM-DD'), for the date-of-birth check. */
  checkInDay: string;
  giveConsent: () => Promise<void>;
  scan: (photo: Blob) => Promise<IdScanResult>;
  record: (body: IdRecordBody) => Promise<void>;
  onError: (err: unknown) => string | null;
  onCancel?: () => void;
}> = ({ idPrefix, first, consent, scanMode, checkInDay, giveConsent, scan, record, onError, onCancel }) => {
  const manualOnly = scanMode !== 'ocr_and_manual';
  const [phase, setPhase] = useState<Phase>(consent.given ? (manualOnly ? 'confirm' : 'choose') : 'consent');
  const [agreed, setAgreed] = useState(false);
  const [kind, setKind] = useState<Kind>('passport');
  const [method, setMethod] = useState<'ocr' | 'manual'>(manualOnly ? 'manual' : 'ocr');
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [read, setRead] = useState<Draft | null>(null);
  const [proof, setProof] = useState<string | null>(null);
  const [lowConfidence, setLowConfidence] = useState(false);
  const [failedReads, setFailedReads] = useState(0);
  const [scansLeft, setScansLeft] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement | null>(null);

  const agree = async () => {
    if (busy || !agreed) return;
    setBusy(true);
    setProblem(null);
    try {
      await giveConsent();
      setPhase(manualOnly ? 'confirm' : 'choose');
    } catch (err) {
      setProblem(onError(err));
    } finally {
      setBusy(false);
    }
  };

  const typeInstead = (why: string | null) => {
    setMethod('manual');
    setProof(null);
    setRead(null);
    setLowConfidence(false);
    setFields({});
    setProblem(why);
    setPhase('confirm');
  };

  const onPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || busy) return;
    setBusy(true);
    setProblem(null);
    setPhase('reading');
    try {
      const photo = await shrinkPhoto(file);
      if (photo.size > ID_PHOTO_MAX_BYTES) {
        setPhase('choose');
        setProblem(t(L, 'idReadFailed'));

        return;
      }
      const out = await scan(photo);
      if (typeof out.scans_left === 'number') setScansLeft(out.scans_left);
      if (!out.ok) {
        const fails = failedReads + 1;
        setFailedReads(fails);
        if (out.reason === 'manual_only' || fails >= READS_BEFORE_MANUAL || out.scans_left === 0) {
          typeInstead(out.reason === 'manual_only' ? null : t(L, 'idReadFailedOften'));
        } else {
          setPhase('choose');
          setProblem(t(L, 'idReadFailed'));
        }

        return;
      }
      const d = draftOf(out.fields);
      setKind(docToKind(out.fields.doc_type, kind));
      setDraft(d);
      setRead(d);
      setProof(out.proof);
      setLowConfidence(out.confidence === 'low');
      setMethod('ocr');
      setFields({});
      setPhase('confirm');
    } catch (err) {
      setPhase('choose');
      if (err instanceof StayPublicError && err.code === 'ID_SCAN_LIMIT') {
        typeInstead(onError(err));

        return;
      }
      setProblem(onError(err));
    } finally {
      setBusy(false);
    }
  };

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft(prev => ({ ...prev, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const bad: Record<string, string> = {};
    if (!draft.first_name.trim()) bad.first_name = t(L, 'idFixField');
    if (!draft.last_name.trim()) bad.last_name = t(L, 'idFixField');
    if (!draft.doc_number.trim()) bad.doc_number = t(L, 'idFixField');
    if (!draft.doc_country) bad.doc_country = t(L, 'idFixField');
    const birth = checkBirth(draft.birth, checkInDay);
    if ('problem' in birth) bad.birth_date = t(L, 'idFixField');
    const expiry = partsToYmd(draft.expiry);
    if (!expiry) bad.expiry_date = t(L, 'idFixField');
    if (Object.keys(bad).length) {
      setFields(bad);

      return;
    }
    const out: IdFields = {
      doc_type: KIND_TO_DOC[kind],
      doc_country: draft.doc_country,
      nationality: draft.nationality || null,
      first_name: draft.first_name.trim(),
      last_name: draft.last_name.trim(),
      doc_number: draft.doc_number.trim(),
      birth_date: 'value' in birth ? birth.value : null,
      expiry_date: expiry,
    };
    const edited = method === 'ocr' && !!read && valuesOf(draft) !== valuesOf(read);
    setBusy(true);
    setProblem(null);
    setFields({});
    try {
      await record({ method, fields: out, edited, proof: method === 'ocr' ? proof : null });
    } catch (err) {
      setBusy(false);
      if (err instanceof StayPublicError && err.status === 400 && Object.keys(err.fields).length) {
        setFields(err.fields);

        return;
      }
      if (err instanceof StayPublicError && err.code === 'ID_PROOF_INVALID') {
        setPhase(manualOnly ? 'confirm' : 'choose');
      }
      setProblem(onError(err));
    }
  };

  const photoInput = (
    <input
      ref={fileRef}
      type="file"
      accept="image/*"
      capture="environment"
      hidden
      data-id-photo-input={idPrefix}
      onChange={onPhoto}
    />
  );

  if (phase === 'consent') {
    return (
      <PersonGrid>
        <Legend>{t(L, 'idConsentTitle', { first })}</Legend>
        <Prose>{consent.text}</Prose>
        <Quiet>{t(L, 'privacyVersion', { version: consent.version })}</Quiet>
        <CheckRow htmlFor={`${idPrefix}-agree`}>
          <input id={`${idPrefix}-agree`} type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
          <span>{t(L, 'idAgree')}</span>
        </CheckRow>
        {problem ? <ErrorText>{problem}</ErrorText> : null}
        <PartyActions>
          <Primary type="button" onClick={agree} disabled={!agreed} aria-busy={busy || undefined}>
            <StatusLabel state={busy ? 'busy' : 'idle'} labels={{ idle: t(L, 'continue'), busy: t(L, 'saving') }} />
          </Primary>
          {onCancel ? (
            <Ghost type="button" onClick={onCancel}>
              {t(L, 'cancel')}
            </Ghost>
          ) : null}
        </PartyActions>
      </PersonGrid>
    );
  }

  const kindControl = (
    <Field>
      <SegmentedControl<Kind>
        label={t(L, 'idDocTypeLabel')}
        value={kind}
        onChange={setKind}
        disabled={busy}
        options={[
          { value: 'passport', label: t(L, 'idDocPassport') },
          { value: 'id_card', label: t(L, 'idDocIdCard') },
          { value: 'other', label: t(L, 'idDocOther') },
        ]}
      />
    </Field>
  );

  if (phase === 'choose' || phase === 'reading') {
    return (
      <PersonGrid aria-busy={phase === 'reading' || undefined}>
        <Legend>{t(L, 'addIdPerson', { first })}</Legend>
        {kindControl}
        <Quiet>{t(L, 'idPhotoHint')}</Quiet>
        <Quiet>{t(L, 'idPhotoPrivacy')}</Quiet>
        {phase === 'reading' ? <Quiet role="status">{t(L, 'idReading')}</Quiet> : null}
        {scansLeft != null && scansLeft <= 2 ? <Quiet>{t(L, 'idScansLeft', { n: scansLeft })}</Quiet> : null}
        {problem ? <ErrorText>{problem}</ErrorText> : null}
        {photoInput}
        <PartyActions>
          <Primary type="button" onClick={() => fileRef.current?.click()} aria-busy={busy || undefined} disabled={busy}>
            <StatusLabel
              state={busy ? 'busy' : 'idle'}
              labels={{ idle: failedReads ? t(L, 'idTakePhotoAgain') : t(L, 'idTakePhoto'), busy: t(L, 'idReading') }}
            />
          </Primary>
          <Secondary type="button" onClick={() => typeInstead(null)} disabled={busy}>
            {t(L, 'idEnterYourself')}
          </Secondary>
          {onCancel ? (
            <Ghost type="button" onClick={onCancel} disabled={busy}>
              {t(L, 'cancel')}
            </Ghost>
          ) : null}
        </PartyActions>
      </PersonGrid>
    );
  }

  const formId = `${idPrefix}-id`;

  return (
    <PersonGrid>
      <Legend>{method === 'ocr' ? t(L, 'idConfirmTitle') : t(L, 'addIdPerson', { first })}</Legend>
      <form id={formId} onSubmit={submit} noValidate>
        {method === 'manual' ? kindControl : null}
        <Quiet>{method === 'ocr' ? t(L, 'idConfirmHint') : t(L, 'idManualOnly')}</Quiet>
        {lowConfidence ? <Quiet role="status">{t(L, 'idLowConfidence')}</Quiet> : null}
        <Field>
          <Label htmlFor={`${formId}-first`}>{t(L, 'idFirstName')}</Label>
          <Input
            id={`${formId}-first`}
            value={draft.first_name}
            onChange={e => set('first_name', e.target.value)}
            autoComplete="off"
            hasError={!!fields.first_name}
            aria-invalid={fields.first_name ? true : undefined}
          />
          <FieldError message={fields.first_name} />
        </Field>
        <Field>
          <Label htmlFor={`${formId}-last`}>{t(L, 'idLastName')}</Label>
          <Input
            id={`${formId}-last`}
            value={draft.last_name}
            onChange={e => set('last_name', e.target.value)}
            autoComplete="off"
            hasError={!!fields.last_name}
            aria-invalid={fields.last_name ? true : undefined}
          />
          <FieldError message={fields.last_name} />
        </Field>
        <Field>
          <Label htmlFor={`${formId}-number`}>{t(L, 'idDocNumber')}</Label>
          <Input
            id={`${formId}-number`}
            value={draft.doc_number}
            onChange={e => set('doc_number', e.target.value.toUpperCase())}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            hasError={!!fields.doc_number}
            aria-invalid={fields.doc_number ? true : undefined}
          />
          <FieldError message={fields.doc_number} />
        </Field>
        <Field>
          <Label htmlFor={`${formId}-country`}>{t(L, 'idDocCountry')}</Label>
          <Select
            id={`${formId}-country`}
            value={draft.doc_country}
            onChange={e => set('doc_country', e.target.value)}
            aria-invalid={fields.doc_country ? true : undefined}
          >
            <option value="">{t(L, 'idChooseCountry')}</option>
            {COUNTRY_OPTIONS.map(c => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </Select>
          <FieldError message={fields.doc_country} />
        </Field>
        <Field>
          <Label htmlFor={`${formId}-nationality`}>{t(L, 'idNationality')}</Label>
          <Select
            id={`${formId}-nationality`}
            value={draft.nationality}
            onChange={e => set('nationality', e.target.value)}
            aria-invalid={fields.nationality ? true : undefined}
          >
            <option value="">{t(L, 'idChooseCountry')}</option>
            {COUNTRY_OPTIONS.map(c => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </Select>
          <FieldError message={fields.nationality} />
        </Field>
        <BirthDateField
          id={`${formId}-birth`}
          legend={t(L, 'idBirthDate')}
          value={draft.birth}
          onChange={v => set('birth', v)}
          error={fields.birth_date}
        />
        <BirthDateField
          id={`${formId}-expiry`}
          legend={t(L, 'idExpiryDate')}
          value={draft.expiry}
          onChange={v => set('expiry', v)}
          error={fields.expiry_date}
        />
        {method === 'ocr' ? <FieldHint>{t(L, 'idPhotoPrivacy')}</FieldHint> : null}
        {problem ? <ErrorText>{problem}</ErrorText> : null}
        {photoInput}
        <PartyActions>
          <Primary type="submit" aria-busy={busy || undefined}>
            <StatusLabel state={busy ? 'busy' : 'idle'} labels={{ idle: t(L, 'idConfirm'), busy: t(L, 'idConfirming') }} />
          </Primary>
          {!manualOnly ? (
            <Secondary type="button" onClick={() => fileRef.current?.click()} disabled={busy}>
              {method === 'ocr' ? t(L, 'idTakePhotoAgain') : t(L, 'idBackToPhoto')}
            </Secondary>
          ) : null}
          {onCancel ? (
            <Ghost type="button" onClick={onCancel} disabled={busy}>
              {t(L, 'cancel')}
            </Ghost>
          ) : null}
        </PartyActions>
      </form>
    </PersonGrid>
  );
};
